import React, { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  HelpCircle, X, Send, Plus, ChevronLeft, Loader2,
  MessageCircle, CheckCircle, Clock, AlertCircle,
} from 'lucide-react';
import { AppDispatch, RootState } from '../../redux';
import {
  createSupportTicket,
  fetchMyTickets,
  fetchTicketById,
  replyToTicket,
  clearSelectedTicket,
  SupportTicket,
} from '../../redux/support/supportSlice';

type View = 'home' | 'tickets' | 'new-ticket' | 'conversation';

const CATEGORIES = [
  'Getting Started',
  'Account Management',
  'Billing & Payments',
  'Clinical Tools',
  'Technical Issue',
  'Other',
];

const STATUS_CONFIG: Record<string, { label: string; color: string; icon: React.ReactNode }> = {
  open:     { label: 'Open',     color: 'bg-blue-100 text-blue-700',   icon: <MessageCircle className="w-3 h-3" /> },
  pending:  { label: 'Pending',  color: 'bg-yellow-100 text-yellow-700', icon: <Clock className="w-3 h-3" /> },
  resolved: { label: 'Resolved', color: 'bg-green-100 text-green-700', icon: <CheckCircle className="w-3 h-3" /> },
  closed:   { label: 'Closed',   color: 'bg-gray-100 text-gray-600',   icon: <X className="w-3 h-3" /> },
};

export const SupportWidget: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { tickets, selectedTicket, isLoading, isSending, error } = useSelector(
    (state: RootState) => (state as any).support
  );

  const [isOpen, setIsOpen] = useState(false);
  const [view, setView] = useState<View>('home');
  const [form, setForm] = useState({ subject: '', category: CATEGORIES[0], message: '', priority: 'normal' });
  const [replyText, setReplyText] = useState('');
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isOpen && view === 'tickets') dispatch(fetchMyTickets());
  }, [isOpen, view, dispatch]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [selectedTicket?.messages]);

  const handleOpen = () => {
    setIsOpen(true);
    setView('home');
  };

  const handleClose = () => {
    setIsOpen(false);
    setView('home');
    setSubmitSuccess(false);
    dispatch(clearSelectedTicket());
  };

  const handleNewTicket = async (e: React.FormEvent) => {
    e.preventDefault();
    const result = await dispatch(createSupportTicket({
      subject: form.subject,
      message: form.message,
      category: form.category,
      priority: form.priority,
    }));
    if (createSupportTicket.fulfilled.match(result)) {
      setSubmitSuccess(true);
      setForm({ subject: '', category: CATEGORIES[0], message: '', priority: 'normal' });
      setTimeout(() => { setSubmitSuccess(false); setView('tickets'); }, 2000);
    }
  };

  const handleOpenTicket = (ticket: SupportTicket) => {
    dispatch(fetchTicketById(ticket._id));
    setView('conversation');
  };

  const handleReply = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!replyText.trim() || !selectedTicket) return;
    const result = await dispatch(replyToTicket({ ticketId: selectedTicket._id, message: replyText }));
    if (replyToTicket.fulfilled.match(result)) setReplyText('');
  };

  const formatDate = (iso: string) => {
    const d = new Date(iso);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  return (
    <>
      {/* Floating Button */}
      <button
        onClick={handleOpen}
        className="fixed bottom-5 right-5 z-50 w-14 h-14 bg-gradient-to-br from-purple-600 to-indigo-600 text-white rounded-full shadow-xl hover:shadow-2xl hover:scale-105 transition-all duration-200 flex items-center justify-center"
      >
        <HelpCircle className="w-6 h-6" />
        {tickets.some((t: SupportTicket) => t.status === 'open') && (
          <span className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 rounded-full text-[10px] flex items-center justify-center font-bold">
            {tickets.filter((t: SupportTicket) => t.status === 'open').length}
          </span>
        )}
      </button>

      {/* Panel */}
      {isOpen && (
        <div className="fixed bottom-24 right-5 z-50 w-[380px] max-h-[600px] bg-white rounded-2xl shadow-2xl border border-gray-100 flex flex-col overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-purple-600 to-indigo-600 px-5 py-4 flex items-center justify-between shrink-0">
            <div className="flex items-center gap-3">
              {view !== 'home' && (
                <button
                  onClick={() => {
                    if (view === 'conversation') { dispatch(clearSelectedTicket()); setView('tickets'); }
                    else setView('home');
                  }}
                  className="text-white/80 hover:text-white"
                >
                  <ChevronLeft className="w-5 h-5" />
                </button>
              )}
              <div>
                <h3 className="text-white font-semibold text-base leading-tight">
                  {view === 'home' && 'Support Center'}
                  {view === 'tickets' && 'My Tickets'}
                  {view === 'new-ticket' && 'New Ticket'}
                  {view === 'conversation' && (selectedTicket?.subject ?? 'Conversation')}
                </h3>
                <p className="text-purple-200 text-xs">We typically reply within a few hours</p>
              </div>
            </div>
            <button onClick={handleClose} className="text-white/80 hover:text-white">
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto">

            {/* Home */}
            {view === 'home' && (
              <div className="p-5 space-y-3">
                <button
                  onClick={() => { setView('new-ticket'); }}
                  className="w-full flex items-center gap-4 p-4 rounded-xl bg-gradient-to-r from-purple-50 to-indigo-50 border border-purple-100 hover:border-purple-300 transition-all group"
                >
                  <div className="w-10 h-10 bg-purple-600 rounded-xl flex items-center justify-center shrink-0 group-hover:scale-105 transition-transform">
                    <Plus className="w-5 h-5 text-white" />
                  </div>
                  <div className="text-left">
                    <p className="font-semibold text-gray-900 text-sm">New Support Request</p>
                    <p className="text-xs text-gray-500">Describe your issue and we'll help</p>
                  </div>
                </button>
                <button
                  onClick={() => { dispatch(fetchMyTickets()); setView('tickets'); }}
                  className="w-full flex items-center gap-4 p-4 rounded-xl bg-gray-50 border border-gray-100 hover:border-gray-300 transition-all group"
                >
                  <div className="w-10 h-10 bg-gray-700 rounded-xl flex items-center justify-center shrink-0 group-hover:scale-105 transition-transform">
                    <MessageCircle className="w-5 h-5 text-white" />
                  </div>
                  <div className="text-left">
                    <p className="font-semibold text-gray-900 text-sm">My Tickets</p>
                    <p className="text-xs text-gray-500">View replies to your support requests</p>
                  </div>
                </button>
                <div className="pt-2 border-t">
                  <p className="text-xs text-gray-400 text-center">
                    Or email <a href="mailto:support@therapistdirect.com" className="text-purple-600 hover:underline">support@therapistdirect.com</a>
                  </p>
                </div>
              </div>
            )}

            {/* Tickets list */}
            {view === 'tickets' && (
              <div className="p-4">
                {isLoading ? (
                  <div className="flex justify-center py-12">
                    <Loader2 className="w-6 h-6 animate-spin text-purple-400" />
                  </div>
                ) : tickets.length === 0 ? (
                  <div className="text-center py-12">
                    <MessageCircle className="w-10 h-10 text-gray-200 mx-auto mb-3" />
                    <p className="text-gray-500 text-sm">No tickets yet</p>
                    <button
                      onClick={() => setView('new-ticket')}
                      className="mt-3 text-purple-600 text-sm hover:underline"
                    >
                      Open a new request
                    </button>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {tickets.map((ticket: SupportTicket) => {
                      const status = STATUS_CONFIG[ticket.status] ?? STATUS_CONFIG.open;
                      return (
                        <button
                          key={ticket._id}
                          onClick={() => handleOpenTicket(ticket)}
                          className="w-full text-left p-3 rounded-xl border border-gray-100 hover:border-purple-200 hover:bg-purple-50/30 transition-all"
                        >
                          <div className="flex items-start justify-between gap-2">
                            <p className="text-sm font-medium text-gray-900 line-clamp-1">{ticket.subject}</p>
                            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium shrink-0 ${status.color}`}>
                              {status.icon}{status.label}
                            </span>
                          </div>
                          <p className="text-xs text-gray-400 mt-1">{ticket.category} · {formatDate(ticket.createdAt)}</p>
                        </button>
                      );
                    })}
                    <button
                      onClick={() => setView('new-ticket')}
                      className="w-full flex items-center justify-center gap-2 py-3 text-sm text-purple-600 hover:text-purple-700 border border-dashed border-purple-200 rounded-xl hover:bg-purple-50 transition-all"
                    >
                      <Plus className="w-4 h-4" /> New Request
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* New ticket form */}
            {view === 'new-ticket' && (
              <div className="p-5">
                {submitSuccess ? (
                  <div className="text-center py-10">
                    <div className="w-14 h-14 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
                      <CheckCircle className="w-7 h-7 text-green-600" />
                    </div>
                    <h4 className="font-semibold text-gray-900 mb-1">Ticket Submitted!</h4>
                    <p className="text-sm text-gray-500">We'll get back to you shortly.</p>
                  </div>
                ) : (
                  <form onSubmit={handleNewTicket} className="space-y-4">
                    <div>
                      <label className="block text-xs font-semibold text-gray-600 mb-1">Subject</label>
                      <input
                        type="text"
                        required
                        value={form.subject}
                        onChange={(e) => setForm({ ...form, subject: e.target.value })}
                        placeholder="Brief description of your issue"
                        className="w-full px-3 py-2.5 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-300"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-gray-600 mb-1">Category</label>
                      <select
                        value={form.category}
                        onChange={(e) => setForm({ ...form, category: e.target.value })}
                        className="w-full px-3 py-2.5 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-300 bg-white"
                      >
                        {CATEGORIES.map((c) => <option key={c}>{c}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-gray-600 mb-1">Priority</label>
                      <select
                        value={form.priority}
                        onChange={(e) => setForm({ ...form, priority: e.target.value })}
                        className="w-full px-3 py-2.5 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-300 bg-white"
                      >
                        <option value="low">Low</option>
                        <option value="normal">Normal</option>
                        <option value="high">High</option>
                        <option value="urgent">Urgent</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-gray-600 mb-1">Message</label>
                      <textarea
                        required
                        rows={4}
                        value={form.message}
                        onChange={(e) => setForm({ ...form, message: e.target.value })}
                        placeholder="Describe your issue in detail..."
                        className="w-full px-3 py-2.5 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-300 resize-none"
                      />
                    </div>
                    {error && (
                      <div className="flex items-center gap-2 text-red-600 text-xs bg-red-50 px-3 py-2 rounded-lg">
                        <AlertCircle className="w-4 h-4 shrink-0" />{error}
                      </div>
                    )}
                    <button
                      type="submit"
                      disabled={isSending}
                      className="w-full py-3 bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-xl text-sm font-semibold hover:from-purple-700 hover:to-indigo-700 transition-all disabled:opacity-50 flex items-center justify-center gap-2"
                    >
                      {isSending ? <><Loader2 className="w-4 h-4 animate-spin" /> Sending…</> : <><Send className="w-4 h-4" /> Submit Request</>}
                    </button>
                  </form>
                )}
              </div>
            )}

            {/* Conversation view */}
            {view === 'conversation' && (
              <div className="flex flex-col h-full">
                {isLoading ? (
                  <div className="flex justify-center py-12">
                    <Loader2 className="w-6 h-6 animate-spin text-purple-400" />
                  </div>
                ) : selectedTicket ? (
                  <>
                    <div className="flex-1 p-4 space-y-3 overflow-y-auto min-h-0" style={{ maxHeight: 340 }}>
                      {(selectedTicket.messages ?? []).length === 0 && (
                        <p className="text-center text-gray-400 text-sm py-6">No messages yet</p>
                      )}
                      {(selectedTicket.messages ?? []).map((msg) => (
                        <div
                          key={msg._id}
                          className={`flex ${msg.sender === 'user' ? 'justify-end' : 'justify-start'}`}
                        >
                          <div
                            className={`max-w-[80%] px-4 py-2.5 rounded-2xl text-sm ${
                              msg.sender === 'user'
                                ? 'bg-gradient-to-br from-purple-600 to-indigo-600 text-white rounded-br-sm'
                                : 'bg-gray-100 text-gray-800 rounded-bl-sm'
                            }`}
                          >
                            <p className="leading-relaxed">{msg.message}</p>
                            <p className={`text-[10px] mt-1 ${msg.sender === 'user' ? 'text-purple-200' : 'text-gray-400'}`}>
                              {formatDate(msg.createdAt)}
                            </p>
                          </div>
                        </div>
                      ))}
                      <div ref={messagesEndRef} />
                    </div>

                    {selectedTicket.status !== 'closed' && selectedTicket.status !== 'resolved' ? (
                      <form onSubmit={handleReply} className="p-3 border-t border-gray-100 flex gap-2 shrink-0">
                        <input
                          type="text"
                          value={replyText}
                          onChange={(e) => setReplyText(e.target.value)}
                          placeholder="Type a message..."
                          className="flex-1 px-3 py-2 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-300"
                        />
                        <button
                          type="submit"
                          disabled={isSending || !replyText.trim()}
                          className="p-2.5 bg-purple-600 text-white rounded-xl hover:bg-purple-700 transition disabled:opacity-40"
                        >
                          {isSending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                        </button>
                      </form>
                    ) : (
                      <div className="p-3 border-t bg-gray-50 text-center">
                        <p className="text-xs text-gray-500">
                          This ticket is {selectedTicket.status}.{' '}
                          <button onClick={() => setView('new-ticket')} className="text-purple-600 hover:underline">Open a new one</button>
                        </p>
                      </div>
                    )}
                  </>
                ) : null}
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};
